import ModelInstance from "./ModelInstance";
import {AdditiveBlending, Box3, MeshBasicMaterial, Vector3} from "three";
import {useEffect, useMemo, useRef} from "react";
import {useScreenConfig} from "../../model/ScreenConfigContext";
import {useAspectRatio} from "../../hooks/useAspectRatio";
import {useSeatConfig} from "../../model/SeatConfigContext";
import {useSightlineGeometry} from "./geometry/useSightlineGeometry";
import {useRoomConfig} from "../../model/RoomConfigContext";

const b = new Box3();
const v = new Vector3();

const sightlineMaterial = new MeshBasicMaterial({color: 0x5050f0, transparent: true, blending: AdditiveBlending, opacity: 0.2, depthWrite: false});

export default function Occupant({object, ...props}) {
    const lineRef = useRef();
    const {distance, numRows, numSeats, preferredSeats} = useSeatConfig();
    const {screenSize, aspectRatio, screenFloorDistance} = useScreenConfig();
    const {frontMargin} = useRoomConfig();
    const aspect = useAspectRatio(aspectRatio);
    const cone = useSightlineGeometry();

    useEffect(() => {
        sightlineMaterial.opacity = 0.5 / (numSeats * numRows);
    }, [numSeats, numRows]);

    const dstPos = useMemo(() => {
        const height = screenSize / aspect * .5 + screenFloorDistance;
        return new Vector3(0, height, frontMargin);
    }, [aspect, frontMargin, screenFloorDistance, screenSize]);

    const eyePos = useMemo(() => {
        if (!object) return new Vector3();
        const v = new Vector3();
        b.setFromObject(object);
        v.y = b.max.y - 7;
        v.z = b.min.z + 10;
        return v;
    }, [object]);

    useEffect(() => {
        const line = lineRef.current;
        const srcPos = line.position;
        srcPos.copy(eyePos);
        line.lookAt(dstPos);
        v.copy(dstPos).applyMatrix4(line.parent.matrixWorld);
        line.scale.x = 5;
        line.scale.y = screenSize / aspect;
        line.scale.z = eyePos.distanceTo(v);
    }, [eyePos, dstPos, distance, numRows, numSeats, preferredSeats, screenSize, aspect]);

    return (
        <group {...props}>
            <ModelInstance object={object}/>
            <mesh geometry={cone} ref={lineRef} material={sightlineMaterial}/>
        </group>
    )
}